/**
 * IssueRowActionsMenu - per-row kebab shared by the On Hold list and the Out of
 * Stock cards.
 *
 * Both lists already carry a prod_order_id, and both already have the two
 * location actions somewhere in the app — but "move to another location" was
 * buried inside the order detail sheet and "split across locations" was only
 * reachable from the Split Orders tab. This puts both one click from the row.
 *
 * Shaped like the Split Orders card kebab (ghost icon button, MoreHorizontal,
 * DropdownMenuContent) so the two Issues surfaces read the same.
 *
 * GATING: the menu renders nothing for a role the resolve board's RoleGuard
 * would reject — same allowlist, one source: RESOLVE_BOARD_ROLES, whose comment
 * carries why hiding beats a link into a silent redirect.
 *
 * The OOS IssueCard root is itself a `role="button"`, so every click here stops
 * propagation — otherwise opening the menu would also open the card.
 */
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GitBranch, MapPin, MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useRole } from '@/hooks/use-role';
import { canOpenResolveBoard } from '@/lib/roles';

interface IssueRowActionsMenuProps {
  /** prod_order id the resolve board is keyed off. */
  prodOrderId: string;
  /** Opens the order detail sheet, where "Move to another location" lives. */
  onMove: () => void;
  disabled?: boolean;
  /** Which list the operator came from, so the board's back link returns there. */
  from: 'on-hold' | 'oos';
}

/**
 * The OOS card root is a `role="button"` that also activates on Enter / Space
 * via onKeyDown. Radix opens the trigger (and fires items) on keydown without
 * stopping propagation, so without this a keyboard user opening the menu would
 * also open the card's detail sheet.
 */
const stopActivationKeys = (e: React.KeyboardEvent) => {
  if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
};

export function IssueRowActionsMenu({
  prodOrderId,
  onMove,
  disabled = false,
  from,
}: IssueRowActionsMenuProps) {
  const { t } = useTranslation('production');
  const { role } = useRole();
  const navigate = useNavigate();

  if (!canOpenResolveBoard(role)) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          aria-label={t('issues.rowActions.more')}
          disabled={disabled}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={stopActivationKeys}
        >
          <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onKeyDown={stopActivationKeys}>
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            onMove();
          }}
        >
          <MapPin className="mr-2 h-4 w-4" aria-hidden="true" />
          {t('issues.rowActions.move')}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/production/issues/resolve/${prodOrderId}?from=${from}`);
          }}
        >
          <GitBranch className="mr-2 h-4 w-4" aria-hidden="true" />
          {t('issues.rowActions.split')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
